import { GridCell, ProspectingEvent, FieldDeposit } from "./types.ts";

export const SYSTEM_SIZE = 20;

const emptyCell = (): GridCell => ({ asteroid: false });

export const emptyGrid = (): GridCell[][] =>
  new Array(SYSTEM_SIZE).fill(0).map(() => new Array(SYSTEM_SIZE).fill(0).map(emptyCell));

const getCoordsByDistance = (startingPoint: { x: number; y: number }, range: number) => {
  const x = startingPoint.x;
  const y = startingPoint.y;
  const coords: Array<{ x: number; y: number }> = [];

  for (let i = -range; i <= range; i++) {
    for (let j = -range; j <= range; j++) {
      const xWithinRange = x + i >= 0 && x + i < SYSTEM_SIZE;
      const yWithinRange = y + j >= 0 && y + j < SYSTEM_SIZE;
      const actualRange = Math.abs(i) === range || Math.abs(j) === range;

      if (xWithinRange && yWithinRange && actualRange) {
        coords.push({ x: x + i, y: y + j });
      }
    }
  }

  return coords;
};

const getEventKey = (event: ProspectingEvent) => `${event.date}@(${event.system.x},${event.system.y})`;

const applyEvent = (grid: GridCell[][]) => (event: ProspectingEvent) => {
  if (event.trace === -1) {
    const coords = [...getCoordsByDistance(event.system, 2), ...getCoordsByDistance(event.system, 1)];
    coords.forEach((coord) => {
      const square = grid[coord.y][coord.x];
      if (!square.asteroid || square.prospected || square.deposit) {
        return;
      }
      square.depositExcluded = true;
    });
  } else if (event.trace === 0) {
    const square = grid[event.system.y][event.system.x];
    square.found = true;
  } else if (event.trace > 0) {
    if (event.trace === 2) {
      const adjacentSquares = getCoordsByDistance(event.system, 1);
      adjacentSquares.forEach((coord) => {
        const square = grid[coord.y][coord.x];
        if (!square.asteroid || square.deposit || square.prospected) {
          return;
        }
        square.depositExcluded = true;
      });
    }
  }

  const eventSquare = grid[event.system.y][event.system.x];
  eventSquare.prospected = true;
  eventSquare.trace = event.trace;
  if (event.deposit && event.deposit.type !== "") {
    eventSquare.deposit = event.deposit;
  }
};

const applyEvents = (grid: GridCell[][], events: ProspectingEvent[]) => {
  const sorted = [...events].sort((a, b) => a.date - b.date);
  sorted.forEach(applyEvent(grid));
  return grid;
};

const applyEventInference = (grid: GridCell[][], events: ProspectingEvent[]) => {
  events
    .filter((event) => event.trace > 0)
    .forEach((event) => {
      const coords = getCoordsByDistance(event.system, event.trace);
      const ringAsteroids = coords.filter((coord) => {
        const cell = grid[coord.y][coord.x];
        return cell.asteroid;
      });

      const knownDepositCount = ringAsteroids.filter((coord) => {
        const cell = grid[coord.y][coord.x];
        return !!cell.deposit || !!cell.found;
      }).length;

      const unresolved = ringAsteroids.filter((coord) => {
        const cell = grid[coord.y][coord.x];
        return !cell.prospected && !cell.depositExcluded && !cell.deposit;
      });

      if (unresolved.length === 0) {
        return;
      }

      const key = getEventKey(event);

      // We can only conclude "indirectFound" when there is no other deposit known
      // at this distance and exactly one unresolved candidate remains. A trace reports
      // the MIN distance to a deposit, so when a deposit is already known at that
      // distance, the trace is already satisfied and the lone unresolved cell could
      // just as well be empty — we must fall through to the "possible" marking.
      if (knownDepositCount === 0 && unresolved.length === 1) {
        const only = unresolved[0];
        const cell = grid[only.y][only.x];
        cell.indirectFound = true;
        if (!cell.indirectFromEventKeys) {
          cell.indirectFromEventKeys = [];
        }
        cell.indirectFromEventKeys.push(key);
        return;
      }

      // Mark all unresolved ring cells as possible. A known deposit at this distance
      // does NOT rule out additional deposits at the same distance, since multiple
      // deposits can share the MIN distance reported by a trace.
      unresolved.forEach((coord) => {
        const cell = grid[coord.y][coord.x];
        if (!cell.possibleEventKeys) {
          cell.possibleEventKeys = [];
        }
        cell.possibleEventKeys.push(key);
      });
    });
};

const gridFromLayout = (layout: string) => {
  const grid = emptyGrid();
  grid.forEach((row, y) => {
    row.forEach((_, x) => {
      grid[y][x].asteroid = layout[y * SYSTEM_SIZE + x] === "a";
    });
  });
  return grid;
};

export function createGrid(layout: string, events: ProspectingEvent[], deposits: FieldDeposit[]) {
  const grid = applyEvents(gridFromLayout(layout), events);

  deposits.forEach(({ size, type, x, y }) => {
    if (x >= 0 && y >= 0 && x < SYSTEM_SIZE && y < SYSTEM_SIZE) {
      grid[y][x].deposit = { type, size };
    }
  });

  applyEventInference(grid, events);

  return grid;
}

export function gridStats(grid: GridCell[][]) {
  const count = {
    empty: 0,
    prospected: 0,
    total: 0,
  };

  grid.forEach((row) => {
    row.forEach((cell) => {
      if (cell.asteroid) {
        count.total += 1;
        if (cell.prospected) {
          count.prospected += 1;
        } else if (!cell.deposit && !cell.depositExcluded) {
          count.empty += 1;
        }
      }
    });
  });

  return count;
}
